// PSAT/NMSQT score report entry (Bluebook / PSAT tab, Sandy 2026-10-27).
// College Board reports each domain as 1 of 7 performance score bands, drawn
// as a 7-box bar. Our levels are the same 7 bands, so a domain's level is the
// number of filled boxes. claude-student-api submitScoreReport places from it.
//
// Ranges: College Board "Data Layout for PSAT 10 and PSAT/NMSQT Electronic
// Score Reports 2025-26". Math and R&W differ only in bands 3 and 4.
export const PSAT_BANDS = {
  reading: ['160-360', '370-410', '420-480', '490-540', '550-600', '610-670', '680-760'],
  math: ['160-360', '370-410', '420-460', '470-540', '550-600', '610-670', '680-760'],
};

// Report order (College Board's tabs: Reading and Writing first). Ids are the
// engine's domain ids, the keys submitScoreReport expects.
export const PSAT_SECTIONS = [
  {
    key: 'reading',
    label: 'Reading and Writing',
    domains: [
      { id: 'information-and-ideas', label: 'Information and Ideas' },
      { id: 'craft-and-structure', label: 'Craft and Structure' },
      { id: 'expression-of-ideas', label: 'Expression of Ideas' },
      { id: 'standard-english-conventions', label: 'Standard English Conventions' },
    ],
  },
  {
    key: 'math',
    label: 'Math',
    domains: [
      { id: 'algebra', label: 'Algebra' },
      { id: 'advanced-math', label: 'Advanced Math' },
      { id: 'problem-solving-and-data-analysis', label: 'Problem-Solving and Data Analysis' },
      { id: 'geometry-and-trigonometry', label: 'Geometry and Trigonometry' },
    ],
  },
];

export const PSAT_DOMAINS = PSAT_SECTIONS.flatMap((s) => s.domains.map((d) => ({ ...d, section: s.key })));

export const rangeFor = (sectionKey, level) => PSAT_BANDS[sectionKey]?.[level - 1] || null;

// A Performance range the student typed ("550-600", "550 – 600", "550 to 600",
// or just "550") -> { level, exact } or null. Scores step by 10, so band n
// covers its low end up to the next band's low end - 10 (360 + 9 still band 1).
// A two-number range is matched by its midpoint; exact = it is the official
// range for that level, so the page can show "matched to ..." otherwise.
export function levelFromRange(sectionKey, text) {
  const bands = PSAT_BANDS[sectionKey];
  const nums = String(text ?? '').match(/\d{3}/g);
  if (!bands || !nums) return null;
  let [lo, hi] = nums.slice(0, 2).map(Number);
  if (hi == null) hi = lo;
  if (lo > hi) [lo, hi] = [hi, lo];
  if (lo < 160 || hi > 760) return null;
  const mid = (lo + hi) / 2;
  const idx = bands.findIndex((b) => { const [a, z] = b.split('-').map(Number); return mid >= a && mid <= z + 9; });
  if (idx < 0) return null;
  return { level: idx + 1, exact: nums.length >= 2 && `${lo}-${hi}` === bands[idx] };
}

// Section scores: 160-760, ending in 0 (digits only, so "690" but not "69O").
export function isSectionScore(value) {
  if (!/^\d{3}$/.test(String(value ?? ''))) return false;
  const n = Number(value);
  return n >= 160 && n <= 760 && n % 10 === 0;
}

export const sectionComplete = (sectionKey, scores, levels) => {
  const section = PSAT_SECTIONS.find((s) => s.key === sectionKey);
  return isSectionScore(scores[sectionKey]) && section.domains.every((d) => Number.isInteger(levels[d.id]));
};

// What the save did to one domain, in the student's words. Never em dashes
// in student copy.
export function outcomeCopy(o) {
  if (!o) return { title: '', detail: '' };
  const lv = (n) => `Level ${n}`;
  switch (o.result) {
    case 'placed':
      return { title: lv(o.to), detail: 'Your starting level.' };
    case 'raised':
      return {
        title: `${lv(o.from)} → ${o.to}`,
        detail: o.restarted
          ? 'Moved up one level. The assignment you were on starts over at the new level. Finished work is saved.'
          : 'Moved up one level.',
      };
    case 'corrected':
      return { title: `${lv(o.from)} → ${o.to}`, detail: 'Updated from your last save.' };
    case 'unchanged':
      if (o.reported > o.to) return { title: lv(o.to), detail: 'Already moved up from your report.' };
      if (o.reported < o.to) return { title: lv(o.to), detail: 'You are already past this level.' };
      return { title: lv(o.to), detail: 'Matches your report.' };
    case 'kept':
      if (o.reason === 'placement_walk') {
        return { title: 'Finish your check', detail: 'You started a short check here. It sets your level when you finish it.' };
      }
      if (o.reason === 'practicing') {
        return { title: lv(o.to), detail: 'You are practicing at the level your report set, so it stays.' };
      }
      return { title: 'Not changed', detail: 'Something went wrong here. Save again to retry.' };
    default:
      return { title: o.to != null ? lv(o.to) : '', detail: '' };
  }
}
